/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/

import Vue from 'vue'
import Router from 'vue-router'
Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Dashboard Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard'
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('./views/pages/dashboard/DashboardAnalytics.vue'),
          meta: {
            rule: 'isGlobal',
            authRequired: true
          }
        },
        {
          path: 'invoices',
          name: 'invoice-list',
          component: () => import('./views/pages/invoice/InvoiceList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'invoices.my_invoices' },
              { title: 'forms.list', active: true }
            ],
            pageTitle: 'invoices.my_invoices',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: 'invoices/view/:id',
          name: 'invoice-view',
          component: () => import('./views/pages/invoice/InvoiceView'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'invoices.my_invoices', url: '/invoices' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'invoices.invoice',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: 'invoices/reserve/view/:id',
          name: 'reserve-invoice-view',
          component: () => import('./views/pages/invoice/ReserveInvoiceView'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'invoices.my_invoices', url: '/invoices' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'invoices.invoice',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: 'services',
          name: 'rendered-service-list',
          component: () => import('./views/pages/service/ServiceList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'services.rendered_services' },
              { title: 'forms.list', active: true }
            ],
            pageTitle: 'services.rendered_services',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: 'services/view/:id',
          name: 'rendered-service-view',
          component: () => import('./views/pages/service/ServiceView'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'services.rendered_services' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'services.rendered_service',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Chat Routes
        // =======================================
        {
          path: 'chat',
          name: 'chat',
          component: () => import('./views/pages/chat/Chat'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'chats.chats', active: true }
            ],
            pageTitle: 'chats.chats',
            rule: 'isGlobal',
            authRequired: true
          }
        },
        // =======================================
        // Profile Routes
        // =======================================
        {
          path: '/profile/settings',
          name: 'profile-settings',
          component: () => import('@/views/pages/profile/UserSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'users.profile' },
              { title: 'users.profile_settings', active: true }
            ],
            pageTitle: 'users.profile_settings',
            rule: 'isGlobal',
            authRequired: true
          }
        },
        // =======================================
        // Notices Routes
        // =======================================
        {
          path: '/profile/notifications',
          name: 'profile-notifications',
          component: () => import('@/views/pages/profile/Notifications.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'users.profile', url: '/profile' },
              { title: 'notices.notices', active: true }
            ],
            pageTitle: 'notices.view_all',
            rule: 'isGlobal',
            authRequired: true
          }
        },
        // =======================================
        // Case Manager Routes
        // =======================================
        {
          path: 'case-manager/invoices',
          name: 'case-manager-invoice-list',
          component: () => import('./views/pages/case-manager/invoice/InvoiceList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'case_manager.case_manager' },
              { title: 'invoices.invoices' },
              { title: 'forms.list', active: true }
            ],
            pageTitle: 'invoices.invoices',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: 'case-manager/invoices/view/:id',
          name: 'case-manager-invoice-view',
          component: () => import('./views/pages/invoice/InvoiceView'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'case_manager.case_manager' },
              { title: 'invoices.invoices', url: '/invoices' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'invoices.invoice',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: 'case-manager/services',
          name: 'case-manager-service-list',
          component: () => import('./views/pages/case-manager/service/ServiceList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'case_manager.case_manager' },
              { title: 'services.rendered_services' },
              { title: 'forms.list', active: true }
            ],
            pageTitle: 'services.rendered_services',
            rule: 'manager',
            authRequired: true
          }
        },
        // =======================================
        // Employees Routes
        // =======================================
        {
          path: '/business/employees',
          name: 'business-employee-list',
          component: () => import('@/views/pages/manager/employees/EmployeeList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'employees.employees' },
              { title: 'employees.list', active: true }
            ],
            pageTitle: 'employees.list',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/employees/view/:id',
          name: 'business-employee-view',
          component: () => import('@/views/pages/manager/employees/EmployeeView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'employees.employees' },
              { title: 'employees.view', active: true }
            ],
            pageTitle: 'employees.view',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/employees/edit/:id',
          name: 'business-employee-edit',
          component: () => import('@/views/pages/manager/employees/EmployeeEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'employees.employees' },
              { title: 'employees.edit', active: true }
            ],
            pageTitle: 'employees.edit',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/employees/add',
          name: 'business-employee-add',
          component: () => import('@/views/pages/manager/employees/EmployeeAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'employees.employees' },
              { title: 'employees.add', active: true }
            ],
            pageTitle: 'employees.add',
            rule: 'manager',
            authRequired: true
          }
        },
        // =======================================
        // Businesses & Services Routes
        // =======================================
        {
          path: '/business',
          name: 'business',
          component: () => import('@/views/pages/manager/businesses/Business.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'places.places', active: true },
              { title: 'businesses.my_businesses', active: true }
            ],
            pageTitle: 'businesses.my_businesses',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/new',
          name: 'new-business',
          component: () => import('@/views/pages/manager/businesses/NewBusiness.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'businesses.new', active: true }
            ],
            pageTitle: 'businesses.new',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/business/places/:placeId/services',
          name: 'business-place-service',
          component: () => import('@/views/pages/manager/businesses/PlaceServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.my_businesses', url: '/businesses/businesses' },
              { title: 'personnel.personnel', active: true }
            ],
            pageTitle: 'personnel.personnel',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/places/edit/:placeId',
          name: 'business-place-edit',
          component: () => import('@/views/pages/manager/businesses/BusinessPlaceEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'places.places' },
              { title: 'forms.edit', active: true }
            ],
            pageTitle: 'places.edit',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/places/add',
          name: 'business-place-add',
          component: () => import('@/views/pages/manager/businesses/BusinessPlaceAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'places.places' },
              { title: 'forms.add', active: true }
            ],
            pageTitle: 'places.add',
            rule: 'manager',
            authRequired: true
          }
        },
        // =============================================================================
        // Business Invoice Routes
        // =============================================================================
        {
          path: '/business/invoices',
          name: 'business-invoice-list',
          component: () => import('@/views/pages/manager/invoices/InvoiceList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'invoices.personnel_invoices', active: true }
            ],
            pageTitle: 'invoices.personnel_invoices',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/invoices/view/:id',
          name: 'business-invoice-view',
          component: () => import('@/views/pages/manager/invoices/InvoiceView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'invoices.personnel_invoices' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'invoices.personnel_invoices',
            rule: 'manager',
            authRequired: true
          }
        },
        {
          path: '/business/invoices/edit/:id',
          name: 'business-invoice-edit',
          component: () => import('@/views/pages/manager/invoices/InvoiceEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'invoices.personnel_invoices' },
              { title: 'forms.edit', active: true }
            ],
            pageTitle: 'invoices.personnel_invoices',
            rule: 'manager',
            authRequired: true
          }
        },
        // =============================================================================
        // Business Payments Routes
        // =============================================================================
        {
          path: '/business/payments',
          name: 'business-payment-list',
          component: () => import('@/views/pages/manager/payments/Payments'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.business' },
              { title: 'payments.payments', active: true }
            ],
            pageTitle: 'payments.payments',
            rule: 'manager',
            authRequired: true
          }
        },
        // =============================================================================
        // Admin Routes
        // =============================================================================
        {
          path: '/admin/businesses/pending',
          name: 'admin-business-pending',
          component: () => import('@/views/pages/admin/business/BusinessPendingList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.admin' },
              { title: 'admin.pending_list', active: true }
            ],
            pageTitle: 'businesses.pending',
            rule: 'admin',
            authRequired: true
          }
        },
        {
          path: '/admin/documents/pending',
          name: 'admin-document-pending',
          component: () => import('@/views/pages/admin/document/DocumentPendingList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.admin' },
              { title: 'admin.pending_list', active: true }
            ],
            pageTitle: 'documents.pending',
            rule: 'admin',
            authRequired: true
          }
        },
        {
          path: '/admin/services-pricing',
          name: 'admin-service-pricing',
          component: () => import('@/views/pages/admin/config/ServicesPricing.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.admin' },
              { title: 'admin.services_pricing', active: true }
            ],
            pageTitle: 'admin.services_pricing',
            rule: 'admin',
            authRequired: true
          }
        },
        {
          path: '/admin/invoices',
          name: 'admin-invoice-list',
          component: () => import('@/views/pages/admin/invoice/InvoiceList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.admin' },
              { title: 'invoices.invoice_list', active: true }
            ],
            pageTitle: 'invoices.invoice_list',
            rule: 'admin',
            authRequired: true
          }
        },
        {
          path: '/admin/invoices/:id',
          name: 'admin-invoice-view',
          component: () => import('@/views/pages/admin/invoice/InvoiceView'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.admin' },
              { title: 'invoices.invoice_list', active: true }
            ],
            pageTitle: 'invoices.invoice',
            rule: 'admin',
            authRequired: true
          }
        },
        // =============================================================================
        // Employee Routes
        // =============================================================================
        {
          path: '/jobs/shifts',
          name: 'employee-shifts-list',
          component: () => import('@/views/pages/operator/shifts/Shifts'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'employees.shifts', active: true }
            ],
            pageTitle: 'employees.shifts',
            rule: 'operator',
            authRequired: true
          }
        },
        {
          path: '/jobs/reserves',
          name: 'employee-reserves',
          component: () => import('@/views/pages/operator/reserves/Reserves'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'reservation.reserves_calender', active: true }
            ],
            pageTitle: 'reservation.reserves_calender',
            rule: 'operator',
            authRequired: true
          }
        },
        {
          path: '/jobs/requests',
          name: 'employee-request-list',
          component: () => import('@/views/pages/operator/requests/RequestsList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'employees.requests', active: true }
            ],
            pageTitle: 'employees.requests',
            rule: 'operator',
            authRequired: true
          }
        },
        {
          path: '/jobs/invoices',
          name: 'employee-invoice-list',
          component: () => import('@/views/pages/operator/invoices/InvoiceList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'invoices.customer_invoices', active: true }
            ],
            pageTitle: 'invoices.customer_invoices',
            rule: 'operator',
            authRequired: true
          }
        },
        {
          path: '/jobs/invoices/view/:id',
          name: 'employee-invoice-view',
          component: () => import('@/views/pages/operator/invoices/InvoiceView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'invoices.customer_invoices' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'invoices.customer_invoices',
            rule: 'managerOrOperator',
            authRequired: true
          }
        },
        {
          path: '/jobs/invoices/edit/:id',
          name: 'employee-invoice-edit',
          component: () => import('@/views/pages/operator/invoices/InvoiceEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'employees.jobs' },
              { title: 'invoices.customer_invoices' },
              { title: 'forms.edit', active: true }
            ],
            pageTitle: 'invoices.customer_invoices',
            rule: 'operator',
            authRequired: true
          }
        },
        // =============================================================================
        // System Routes
        // =============================================================================

        {
          path: '/system/roles',
          name: 'system-role',
          component: () => import('@/views/pages/system/roles/RoleView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.system' },
              { title: 'system.roles.roles' },
              { title: 'system.common.roles_list', active: true }
            ],
            pageTitle: 'system.common.roles_list',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/enums',
          name: 'system-enum',
          component: () => import('@/views/pages/system/enums/EnumView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.system' },
              { title: 'system.enums' },
              { title: 'system.common.enums_list', active: true }
            ],
            pageTitle: 'system.common.enums_list',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/configs',
          name: 'system-config',
          component: () => import('@/views/pages/system/configs/ConfigView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.system' },
              { title: 'system.configs' },
              { title: 'system.common.configs_list', active: true }
            ],
            pageTitle: 'system.common.configs_list',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/users',
          name: 'system-user-list',
          component: () => import('@/views/pages/system/users/UserList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'system.system' },
              { title: 'system.users' },
              { title: 'system.common.users_list', active: true }
            ],
            pageTitle: 'system.common.users_list',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/users/view/:id',
          name: 'system-user-view',
          component: () => import('@/views/pages/system/users/UserView.vue'),
          meta: {
            breadcrumb: [{ title: 'Home', url: '/' }, { title: 'User' }, { title: 'View', active: true }],
            pageTitle: 'User View',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/users/edit/:id',
          name: 'system-user-edit',
          component: () => import('@/views/pages/system/users/user-edit/UserEdit.vue'),
          meta: {
            breadcrumb: [{ title: 'Home', url: '/' }, { title: 'User' }, { title: 'Edit', active: true }],
            pageTitle: 'User Edit',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses',
          name: 'system-business-list',
          component: () => import('@/views/pages/system/businesses/business-list/BusinessList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'general.system' },
              { title: 'businesses.businesses' },
              { title: 'businesses.list', active: true }
            ],
            pageTitle: 'businesses.list',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses/add/',
          name: 'system-business-add',
          component: () => import('@/views/pages/system/businesses/BusinessAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'general.system' },
              { title: 'businesses.businesses' },
              { title: 'businesses.add', active: true }
            ],
            pageTitle: 'businesses.add',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses/view/:id',
          name: 'system-business-view',
          component: () => import('@/views/pages/system/businesses/BusinessView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'general.system' },
              { title: 'businesses.businesses' },
              { title: 'forms.view', active: true }
            ],
            pageTitle: 'businesses.view',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses/edit/:id',
          name: 'system-business-edit',
          component: () => import('@/views/pages/system/businesses/business-edit/BusinessEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.businesses' },
              { title: 'forms.edit', active: true }
            ],
            pageTitle: 'businesses.edit',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses/edit/:businessId/place/add',
          name: 'system-business-place-add',
          component: () => import('@/views/pages/system/businesses/business-edit/BusinessPlaceAdd'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.businesses' },
              { title: 'forms.add', active: true },
              { title: 'places.place', active: true }
            ],
            pageTitle: 'places.add',
            rule: 'system',
            authRequired: true
          }
        },
        {
          path: '/system/businesses/edit/:businessId/place/:placeId',
          name: 'system-business-place-edit',
          component: () => import('@/views/pages/system/businesses/business-edit/BusinessPlaceEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'businesses.businesses' },
              { title: 'forms.edit', active: true },
              { title: 'places.place', active: true }
            ],
            pageTitle: 'places.edit',
            rule: 'system',
            authRequired: true
          }
        },

        {
          path: '/system/comments',
          name: 'system-comment-list',
          component: () => import('@/views/pages/system/comments/CommentList'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'general.system' },
              { title: 'comments.comments', active: true }
            ],
            pageTitle: 'comments.comments',
            rule: 'system',
            authRequired: true
          }
        },
        // =============================================================================
        // Health Service Routes
        // =============================================================================
        // =======================================
        // Nursing Routes
        // =======================================

        {
          path: '/health-service/nursing/injection',
          name: 'health-service-nursing-injection',
          component: () => import('@/views/pages/health-service/nursing/Injection.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.nursing' },
              { title: 'places.subjects.injection', active: true }
            ],
            pageTitle: 'places.subjects.injection',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/nursing/dressing',
          name: 'health-service-nursing-dressing',
          component: () => import('@/views/pages/health-service/nursing/Dressing.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.nursing' },
              { title: 'places.subjects.dressing', active: true }
            ],
            pageTitle: 'places.subjects.dressing',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/nursing/catheterization',
          name: 'health-service-nursing-catheterization',
          component: () => import('@/views/pages/health-service/nursing/Catheterization.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.nursing' },
              { title: 'places.subjects.catheterization', active: true }
            ],
            pageTitle: 'places.subjects.catheterization',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/nursing/professional-caring',
          name: 'health-service-nursing-professional-caring',
          component: () => import('@/views/pages/health-service/nursing/ProfessionalCaring.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.nursing' },
              {
                title: 'places.subjects.professional_caring',
                active: true
              }
            ],
            pageTitle: 'places.subjects.professional_caring',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/nursing/ambulance',
          name: 'health-service-nursing-ambulance',
          component: () => import('@/views/pages/health-service/nursing/Ambulance.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.nursing' },
              {
                title: 'places.subjects.ambulance',
                active: true
              }
            ],
            pageTitle: 'places.subjects.ambulance',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Pack Routes
        // =======================================
        {
          path: '/health-service/pack/coronavirus',
          name: 'health-service-pack-coronavirus',
          component: () => import('@/views/pages/health-service/pack/Coronavirus'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.coronavirus', active: true }
            ],
            pageTitle: 'places.subjects.coronavirus',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/elderly',
          name: 'health-service-pack-elderly',
          component: () => import('@/views/pages/health-service/pack/Elderly'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.elderly', active: true }
            ],
            pageTitle: 'places.subjects.elderly',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/incurable-patient',
          name: 'health-service-pack-incurable-patient',
          component: () => import('@/views/pages/health-service/pack/IncurablePatient'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.incurable_patient', active: true }
            ],
            pageTitle: 'places.subjects.incurable_patient',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/newborn-care',
          name: 'health-service-pack-newborn-care',
          component: () => import('@/views/pages/health-service/pack/NewbornCare'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.newborn_care', active: true }
            ],
            pageTitle: 'places.subjects.newborn_care',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/pregnancy-period',
          name: 'health-service-pack-pregnancy-period',
          component: () => import('@/views/pages/health-service/pack/PregnancyPeriod'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.pregnancy_period', active: true }
            ],
            pageTitle: 'places.subjects.pregnancy_period',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/child-birth-at-home',
          name: 'health-service-pack-child-birth-at-home',
          component: () => import('@/views/pages/health-service/pack/ChildBirthAtHome'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.child_birth_at_home', active: true }
            ],
            pageTitle: 'places.subjects.child_birth_at_home',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/recovery-period-after-surgery',
          name: 'health-service-pack-recovery-period-after-surgery',
          component: () => import('@/views/pages/health-service/pack/RecoveryPeriodAfterSurgery'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.recovery_period_after_surgery', active: true }
            ],
            pageTitle: 'places.subjects.recovery_period_after_surgery',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/icu-care-at-home',
          name: 'health-service-pack-icu-care-at-home',
          component: () => import('@/views/pages/health-service/pack/IcuCareAtHome'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.icu_care_at_home', active: true }
            ],
            pageTitle: 'places.subjects.icu_care_at_home',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/pack/case-manager',
          name: 'health-service-pack-case-manager',
          component: () => import('@/views/pages/health-service/pack/CaseManager'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.pack' },
              { title: 'places.subjects.case_manager', active: true }
            ],
            pageTitle: 'places.subjects.case_manager',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Drug And Cosmetic Routes
        // =======================================
        {
          path: '/health-service/drug-and-cosmetic/drug',
          name: 'health-service-drug-and-cosmetic-drug',
          component: () => import('@/views/pages/health-service/drug-and-cosmetic/Drug.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.drug_and_cosmetic' },
              { title: 'places.subjects.drug', active: true }
            ],
            pageTitle: 'places.subjects.drug',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/drug-and-cosmetic/cosmetic',
          name: 'health-service-drug-and-cosmetic-cosmetic',
          component: () => import('@/views/pages/health-service/drug-and-cosmetic/Cosmetic.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.drug_and_cosmetic' },
              { title: 'places.subjects.cosmetic', active: true }
            ],
            pageTitle: 'places.subjects.cosmetic',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/drug-and-cosmetic/herbal-medicine',
          name: 'health-service-drug-and-cosmetic-herbal-medicine',
          component: () => import('@/views/pages/health-service/drug-and-cosmetic/HerbalMedicine.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.drug_and_cosmetic' },
              { title: 'places.subjects.herbal_medicine', active: true }
            ],
            pageTitle: 'places.subjects.herbal_medicine',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/drug-and-cosmetic/medicinal-plant',
          name: 'health-service-drug-and-cosmetic-medicinal-plant',
          component: () => import('@/views/pages/health-service/drug-and-cosmetic/MedicinalPlant'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.drug_and_cosmetic' },
              { title: 'places.subjects.medicinal_plant', active: true }
            ],
            pageTitle: 'places.subjects.medicinal_plant',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Equipment Routes
        // =======================================
        {
          path: '/health-service/equipment/rent',
          name: 'health-service-equipment-rent',
          component: () => import('@/views/pages/health-service/equipment/Rent.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'places.categories.equipment' },
              { title: 'places.subjects.rent', active: true }
            ],
            pageTitle: 'places.subjects.rent',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/equipment/buy',
          name: 'health-service-equipment-buy',
          component: () => import('@/views/pages/health-service/equipment/Buy.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'places.categories.equipment' },
              { title: 'places.subjects.buy', active: true }
            ],
            pageTitle: 'places.subjects.buy',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Imaging Routes
        // =======================================
        {
          path: '/health-service/imaging/ecg',
          name: 'health-service-imaging-ecg',
          component: () => import('@/views/pages/health-service/imaging/Ecg.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.imaging' },
              { title: 'places.subjects.ecg', active: true }
            ],
            pageTitle: 'places.subjects.ecg',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/imaging/brain-tape',
          name: 'health-service-imaging-brain-tape',
          component: () => import('@/views/pages/health-service/imaging/BrainTape.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.imaging' },
              { title: 'places.subjects.brain_tape', active: true }
            ],
            pageTitle: 'places.subjects.brain_tape',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/imaging/sonography',
          name: 'health-service-imaging-sonography',
          component: () => import('@/views/pages/health-service/imaging/Sonography.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.imaging' },
              { title: 'places.subjects.sonography', active: true }
            ],
            pageTitle: 'places.subjects.sonography',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/imaging/portable-radiography',
          name: 'health-service-imaging-portable-radiography',
          component: () => import('@/views/pages/health-service/imaging/PortableRadiography.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.imaging' },
              { title: 'places.subjects.portable_radiography', active: true }
            ],
            pageTitle: 'places.subjects.portable_radiography',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Laboratory Routes
        // =======================================
        {
          path: '/health-service/laboratory/check-up',
          name: 'health-service-laboratory-check-up',
          component: () => import('@/views/pages/health-service/laboratory/CheckUp.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.laboratory' },
              { title: 'places.subjects.check_up', active: true }
            ],
            pageTitle: 'places.subjects.check_up',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/laboratory/corona-test',
          name: 'health-service-laboratory-corona-test',
          component: () => import('@/views/pages/health-service/laboratory/CoronaTest.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.laboratory' },
              { title: 'places.subjects.corona_test', active: true }
            ],
            pageTitle: 'places.subjects.corona_test',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/laboratory/medical-diagnostic-laboratory',
          name: 'health-service-laboratory-medical-diagnostic-laboratory',
          component: () => import('@/views/pages/health-service/laboratory/MedicalDiagnosticLab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.laboratory' },
              { title: 'places.subjects.medical_diagnostic_lab', active: true }
            ],
            pageTitle: 'places.subjects.medical_diagnostic_lab',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Doctors Routes
        // =======================================
        {
          path: '/health-service/doctors/general-doctor',
          name: 'health-service-doctors-general-doctor',
          component: () => import('@/views/pages/health-service/doctors/GeneralDoctor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.doctors' },
              { title: 'places.subjects.general_doctor', active: true }
            ],
            pageTitle: 'places.subjects.general_doctor',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/doctors/specialist-doctor',
          name: 'health-service-doctors-specialist-doctor',
          component: () => import('@/views/pages/health-service/doctors/SpecialistDoctor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.doctors' },
              { title: 'places.subjects.specialist_doctor', active: true }
            ],
            pageTitle: 'places.subjects.specialist_doctor',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/doctors/traditional-medicine',
          name: 'health-service-doctors-traditional-medicine',
          component: () => import('@/views/pages/health-service/doctors/TraditionalMedicine.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.doctors' },
              { title: 'places.subjects.traditional_medicine', active: true }
            ],
            pageTitle: 'places.subjects.traditional_medicine',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/doctors/occupational-medicine',
          name: 'health-service-doctors-occupational-medicine',
          component: () => import('@/views/pages/health-service/doctors/OccupationalMedicine.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.doctors' },
              { title: 'places.subjects.occupational_medicine', active: true }
            ],
            pageTitle: 'places.subjects.occupational_medicine',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Consulting Routes
        // =======================================
        {
          path: '/health-service/consulting/doctor-general',
          name: 'health-service-consulting-doctor-general',
          component: () => import('@/views/pages/health-service/consulting/DoctorGeneral'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.doctor_general', active: true }
            ],
            pageTitle: 'places.subjects.doctor_general',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/doctor-specialist',
          name: 'health-service-consulting-doctor-specialist',
          component: () => import('@/views/pages/health-service/consulting/DoctorSpecialist'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.doctor_specialist', active: true }
            ],
            pageTitle: 'places.subjects.doctor_specialist',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/doctor-traditional-medicine',
          name: 'health-service-consulting-doctor-traditional-medicine',
          component: () => import('@/views/pages/health-service/consulting/DoctorTraditionalMedicine'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.doctor_traditional_medicine', active: true }
            ],
            pageTitle: 'places.subjects.doctor_traditional_medicine',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/dentistical',
          name: 'health-service-consulting-dentistical',
          component: () => import('@/views/pages/health-service/consulting/Dentistical.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.dentistical', active: true }
            ],
            pageTitle: 'places.subjects.dentistical',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/nutrition',
          name: 'health-service-consulting-nutrition',
          component: () => import('@/views/pages/health-service/consulting/Nutrition.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.nutrition', active: true }
            ],
            pageTitle: 'places.subjects.nutrition',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/nursing',
          name: 'health-service-consulting-nursing',
          component: () => import('@/views/pages/health-service/consulting/Nursing.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.nursing', active: true }
            ],
            pageTitle: 'places.subjects.nursing',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/medicinal',
          name: 'health-service-consulting-medicinal',
          component: () => import('@/views/pages/health-service/consulting/Medicinal.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.medicinal', active: true }
            ],
            pageTitle: 'places.subjects.medicinal',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/psychology',
          name: 'health-service-consulting-psychology',
          component: () => import('@/views/pages/health-service/consulting/Psychology.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.psychology', active: true }
            ],
            pageTitle: 'places.subjects.psychology',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/sanitary',
          name: 'health-service-consulting-sanitary',
          component: () => import('@/views/pages/health-service/consulting/Sanitary.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.sanitary', active: true }
            ],
            pageTitle: 'places.subjects.sanitary',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/consulting/insurance',
          name: 'health-service-consulting-insurance',
          component: () => import('@/views/pages/health-service/consulting/Insurance.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.consulting' },
              { title: 'places.subjects.insurance', active: true }
            ],
            pageTitle: 'places.subjects.insurance',
            rule: 'isPublic',
            authRequired: true
          }
        },
        // =======================================
        // Accompanying The Patient Routes
        // =======================================
        {
          path: '/health-service/accompanying-the-patient/hospitals-and-clinic',
          name: 'health-service-accompanying-the-patient-hospitals-and-clinic',
          component: () =>
            import('@/views/pages/health-service/accompanying-the-patient/HospitalsAndClinic.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.accompanying_the_patient' },
              { title: 'places.subjects.hospitals_and_clinic', active: true }
            ],
            pageTitle: 'places.subjects.hospitals_and_clinic',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/accompanying-the-patient/para-clinic',
          name: 'health-service-accompanying-the-patient-para-clinic',
          component: () => import('@/views/pages/health-service/accompanying-the-patient/ParaClinic.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.accompanying_the_patient' },
              { title: 'places.subjects.para_clinic', active: true }
            ],
            pageTitle: 'places.subjects.para_clinic',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/accompanying-the-patient/para-medical',
          name: 'health-service-accompanying-the-patient-para-medical',
          component: () => import('@/views/pages/health-service/accompanying-the-patient/ParaMedical.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.accompanying_the_patient' },
              { title: 'places.subjects.para_medical', active: true }
            ],
            pageTitle: 'places.subjects.para_medical',
            rule: 'isPublic',
            authRequired: true
          }
        },

        // =======================================
        // Health Tourism Routes
        // =======================================
        {
          path: '/health-service/tourism/medical',
          name: 'health-service-tourism-medical',
          component: () => import('@/views/pages/health-service/tourism/MedicalServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.medical', active: true }
            ],
            pageTitle: 'places.subjects.medical',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/tourism/complementary',
          name: 'health-service-tourism-complementary',
          component: () => import('@/views/pages/health-service/tourism/ComplementaryMedicalServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.complementary', active: true }
            ],
            pageTitle: 'places.subjects.complementary',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/tourism/licensing',
          name: 'health-service-tourism-licensing',
          component: () => import('@/views/pages/health-service/tourism/LicensingServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.licensing', active: true }
            ],
            pageTitle: 'places.subjects.licensing',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/tourism/transportation',
          name: 'health-service-tourism-transportation',
          component: () => import('@/views/pages/health-service/tourism/TransportationServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.transportation', active: true }
            ],
            pageTitle: 'places.subjects.transportation',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/tourism/ancillary',
          name: 'health-service-tourism-ancillary',
          component: () => import('@/views/pages/health-service/tourism/AncillaryServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.ancillary', active: true }
            ],
            pageTitle: 'places.subjects.ancillary',
            rule: 'isPublic',
            authRequired: true
          }
        },
        {
          path: '/health-service/tourism/accommodation',
          name: 'health-service-tourism-accommodation',
          component: () => import('@/views/pages/health-service/tourism/AccommodationServices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'health_services.health_services' },
              { title: 'places.categories.tourism' },
              { title: 'places.subjects.accommodation', active: true }
            ],
            pageTitle: 'places.subjects.accommodation',
            rule: 'isPublic',
            authRequired: true
          }
        }
        // =======================================
        // Reservation Routes
        // =======================================
        // {
        //   path: '/reservation/resources',
        //   name: 'reservation-resource-list',
        //   component: () => import('@/views/pages/reservation/ReservationResourceList.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'reservation.reservations' },
        //       { title: 'reservation.resources', active: true }
        //     ],
        //     pageTitle: 'reservation.resources',
        //     rule: 'admin',
        //     authRequired: true
        //   }
        // },
        // {
        //   path: '/reservation/resources/add',
        //   name: 'reservation-resource-add',
        //   component: () => import('@/views/pages/reservation/ReservationResourceAdd.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'reservation.reservations' },
        //       { title: 'reservation.resources' },
        //       { title: 'reservation.resource.add', active: true }
        //     ],
        //     pageTitle: 'reservation.resource.add',
        //     rule: 'admin',
        //     authRequired: true
        //   }
        // },
        // {
        //   path: '/reservation/resources/edit',
        //   name: 'reservation-resource-edit',
        //   component: () => import('@/views/pages/reservation/ReservationResourceAdd.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'reservation.reservations' },
        //       { title: 'reservation.resources' },
        //       { title: 'reservation.resource.edit', active: true }
        //     ],
        //     pageTitle: 'reservation.resource.edit',
        //     rule: 'admin',
        //     authRequired: true
        //   }
        // },
        // {
        //   path: '/reservations',
        //   name: 'reservations',
        //   component: () => import('@/views/pages/reservation/ReservationsList.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'reservation.reservations' },
        //       { title: 'reservation.resources', active: true }
        //     ],
        //     pageTitle: 'reservation.resources',
        //     rule: 'isPublic',
        //     authRequired: true
        //   }
        // },
        // {
        //   path: '/reservation/reserve/:id',
        //   name: 'reservation-reserve',
        //   component: () => import('@/views/pages/reservation/ReservationSelect.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'reservation.reservations' },
        //       { title: 'reservation.reserve', active: true }
        //     ],
        //     pageTitle: 'reservation.reserve',
        //     rule: 'isPublic',
        //     authRequired: true
        //   }
        // }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '/r/:code',
          name: 'referral-code',
          component: () => import('./views/pages/affiliate/ReferralCode'),
          meta: {
            rule: ['*']
          }
        },
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/pages/login',
          name: 'page-login',
          component: () => import('@/views/pages/auth/login/Login.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/register',
          name: 'page-register',
          component: () => import('@/views/pages/auth/register/Register.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/auth/ForgotPassword.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/reset-password/:phone',
          name: 'page-reset-password',
          component: () => import('@/views/pages/auth/ResetPassword.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/confirmation/:phone',
          name: 'page-confirmation',
          component: () => import('@/views/pages/auth/Confirmation.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: ['*']
          }
        },
        {
          path: '/pages/set-province',
          name: 'page-set-province',
          component: () => import('@/views/pages/auth/SetProvince.vue'),
          meta: {
            rule: ['*']
          },
          rule: 'isPublic',
          authRequired: true
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-process')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

router.beforeEach((to, from, next) => {
  const appLoading = document.getElementById('loading-process')
  if (appLoading) {
    appLoading.style.display = 'block'
  }
  if (
    to.path === '/pages/login' ||
    to.path === '/pages/forgot-password' ||
    to.path === '/pages/error-404' ||
    to.path === '/pages/error-500' ||
    to.path === '/pages/register' ||
    to.path === '/pages/comingsoon' ||
    to.path === '/pages/confirmation'
  ) {
    return next()
  }

  // If auth required, check login. If login fails redirect to login page
  if (to.meta.authRequired) {
    if (!(localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo'))) {
      return router.push({ path: '/pages/login', query: { to: to.path } })
    }

    const userData = JSON.parse(localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo'))
    if (!userData.province) {
      return router.push({ path: '/pages/set-province', query: { to: to.path } })
    }
  }

  return next()
})

export default router
